import { Pagination } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { VALID_LIST_PAGE_SIZES } from "./redux/global-map.constants"

const PAGE_SIZE_OPTIONS = VALID_LIST_PAGE_SIZES.map(String)

interface ListPaginationFooterProps {
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  // Fix (review round) — disables the whole control (numbered pages, Prev/
  // Next, the size changer) while a fetch for a different page/size is
  // still in flight. Reinstates the re-entrancy guard the old plain
  // Previous/Next `<Button disabled={...}>`s had, lost when they were
  // replaced by this component; `ListTab.tsx` feeds it from
  // `useListFeatures`'s own `status` ("loading"/"refreshing"), not a new
  // signal. Optional/defaults `false` so every existing caller/test that
  // predates this fix keeps working unchanged.
  disabled?: boolean
}

/**
 * The List tab's real page-number pagination control (Task 9), replacing
 * `ListTab.tsx`'s earlier plain Previous/Next buttons: a "Showing X–Y of N
 * records" range on the left and an antd `Pagination` (numbered pages plus a
 * size changer, 5/10/20/50) near the right, inside the table card.
 *
 * Fully controlled — `page`/`pageSize`/`total` come from `useListFeatures`'s
 * own state, and every change is reported upward rather than held locally,
 * the same "hooks own behavior, components stay dumb" shape `setPage`/
 * `setPageSize` already follow there.
 */
export function ListPaginationFooter({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  disabled = false
}: ListPaginationFooterProps) {
  const { t } = useTranslation()
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  return (
    <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
      <span aria-live="polite" data-testid="global-map-list-showing">
        {total === 0
          ? t("globalMap.list.showingZero")
          : t("globalMap.list.showingRange", { from, to, total })}
      </span>
      <Pagination
        current={page}
        pageSize={pageSize}
        total={total}
        disabled={disabled}
        showSizeChanger
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onChange={(nextPage, nextPageSize) => {
          // A page-size change starts a new query — the previous page number
          // rarely still means anything at a different size (page 3 of 10
          // isn't page 3 of 20), so this always lands back on page 1, the
          // same "changing the query resets to page 1" rule every other List
          // tab trigger (`useListFeatures.ts`'s six filter/search/sort/etc.
          // effects) already follows. `onPageSizeChange` is dispatched first
          // so a caller reading both in the same tick sees the new size
          // already applied before the reset.
          if (nextPageSize !== pageSize) {
            onPageSizeChange(nextPageSize)
            onPageChange(1)
          } else {
            onPageChange(nextPage)
          }
        }}
        locale={{ items_per_page: t("globalMap.list.perPage") }}
      />
    </div>
  )
}

export default ListPaginationFooter
